import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap-modal'
import GameDetails from './game-details'
import GameAnnouncements from './game-announcements'
import GameDetailsFooter from './game-details-footer'
import GameEvents from './game-events'
import GameNotes from './game-notes'
import GameReviews from './game-reviews'
import GameDrops from './game-drops'
import CancelModal from '../cancel-open-game-modal'
import PlayerModal from '../player-modal'
import PaymentModal from '../../containers/payment-modal'
import FlashAlert from '../flash-alert'
import FirestoreChannel from '../../containers/firestore-channel'
import RecurringReservations from '../../containers/recurring-reservations'
import Cable from 'actioncable'
import debounce from 'lodash.debounce'
import GameWaitingPlayers from "./game-waiting-players";
import GoogleConflictWarning from '../google-conflict-warning'
import { ajaxGet } from '../../ajax'

function GameModal(props) {
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showPlayer, setShowPlayer] = useState()
  const [showEvents, setShowEvents] = useState(false)
  const [showNotes, setShowNotes] = useState(false)
  const [showReviews, setShowReviews] = useState(false)
  const [showReccurings, setShowRecurrings] = useState(false)
  const [showDrops, setShowDrops] = useState(false)
  const [showWaitList, setShowWaitList] = useState(false)
  const [canExpand, setCanExpand] = useState(true)
  const [googleConflict, setGoogleConflict] = useState()
  const [showAnnouncements, setShowAnnouncements] = useState(false)
  const [alert, setAlert] = useState()
  const game = props.game

  useEffect(() => {
    const cable = Cable.createConsumer(props.socketUrl)
    const gameBatchPaymentChannel = cable.subscriptions.create({
      channel: 'BatchPaymentsChannel',
      open_game_id: props.gameId
    }, {
      received: (data) => { props.onGamePaymentSuccess(data.message) }
    })
    const gameWaitingPlayersChannel = cable.subscriptions.create({
      channel: 'WaitingPlayersChannel',
      open_game_id: props.gameId
    }, {
      received: (data) => {
        props.onSetWaitingPlayers(data.message.waiting_players)
      }
    })
    return () => { cable.subscriptions.remove(gameBatchPaymentChannel) }
    return () => { cable.subscriptions.remove(gameWaitingPlayersChannel) }
  }, [])

  useEffect(() => {
    props.loadGame(props.gameId)
    return () => props.clearGame()
  }, [])

  useEffect(() => { setAlert(props.alert) }, [props.alert])

  useEffect(() => {
    if (game && props.activeTab == 'game-messages') readMessages()
    if (game && props.activeTab == 'game-comment') setShowNotes(true)
    if (game && props.activeTab == 'game-reviews') setShowReviews(true)
    if (game) setCanExpand(game.number_of_players >= game.max_number_of_players)
  }, [game])

  const isActive = (tabName) => {
    return (tabName == props.activeTab) ? ' active in' : ''
  }

  const onShowPlayer = (playerId) => {
    setShowPlayer(playerId)
  }

  const updatePlayer = (newPlayer) => {
    const fullName = [newPlayer.first_name, newPlayer.last_name].filter(n => n).join(', ')
    props.onUpdatePlayer({
      id: showPlayer,
      name: fullName,
      phone: newPlayer.phone,
      display_name: newPlayer.display_name,
      first_name: newPlayer.first_name,
      last_name: newPlayer.last_name,
      captain: newPlayer.captain
    })
    setShowPlayer(null)
    props.showAlert({ text: 'Client update success', type: 'success' })
  }

  const readMessages = () => {
    if (game && game.channel_id) props.readMessages()
  }

  const forceConfirm = () => {
    props.onConfirm({ confirmed: !game.approved })
    props.onClose()
  }

  const checkConflicts = () => {
    ajaxGet('/admins/google_calendars/events?' + new URLSearchParams({ game_id: game.id }))
    .then(response => response.json())
    .then(data => {
      if (data.alert)
        setAlert(data.alert)
      else {
        const events = data.events.filter((e) => e.resourceIds.includes(game.field_id))
        events.length ? setGoogleConflict({gameName: events[0].title}) : forceConfirm()
      }
    })
  }

  const confirmGame = () => {
    game.google_calendar_id && !game.approved ? checkConflicts() : forceConfirm()
  }

  const confirmWithFacility = () => {
    props.updateGame({ confirmed_with_facility: !game.confirmed_with_facility })
    props.onClose()
  }

  const preBook = () => {
    props.updateGame({ pre_booked: !game.pre_booked })
    props.onClose()
  }

  const createChannel = () => {
    props.onCreateChannel({game_id: game.id})
  }

  const handleDebouncedClick = (func) => {
    props.setGameDetailsLoading(true)
    debounce(func, 300)()
  }

  if (!game) return null

  return <Modal container={document.body} show={!!game} onHide={props.onClose} dialogClassName='openGameModal'>
    { showCancelModal && <CancelModal game={game} onClose={() => setShowCancelModal(false)} onCancel={props.onCancel} /> }
    { showPlayer && <PlayerModal playerId={showPlayer} onClose={() => setShowPlayer(null)} onUpdate={updatePlayer} /> }
    { !!props.payment && 
      <PaymentModal
        game={game}
        playerReservation={props.player_reservations.find(pr => pr.id == props.payment)}
        socketUrl={props.socketUrl}
        onClose={() => props.onClearPayment()}
      /> 
    }
    <Modal.Header closeButton>
      <Modal.Title>Open Game</Modal.Title>
    </Modal.Header>
    <ul className='nav nav-tabs'>
      <li className={isActive('game-details')} role='presentation'>
        <a data-toggle='tab' href='#game-details'>Details</a>
      </li>
      <li className={isActive('game-history')} role='presentation'>
        <a data-toggle='tab' href='#game-history' onClick={() => setShowEvents(true)}>History</a>
      </li>
      <li className={isActive('game-recurrings')} role='presentation'>
        <a data-toggle='tab' href='#game-recurrings' onClick={() => setShowRecurrings(true)}>Recurring</a>
      </li>
      <li className={isActive('game-comment')} role='presentation'>
        <a data-toggle='tab' href='#game-comment' onClick={() => setShowNotes(true)}>Notes</a>
      </li>
      <li className={isActive('game-reviews')} role='presentation'>
        <a data-toggle='tab' href='#game-reviews' onClick={() => setShowReviews(true)}>Reviews</a>
      </li>
      <li className={isActive('game-messages')} role='presentation'>
        <a data-toggle='tab' href='#game-messages' onClick={readMessages}>Messages</a>
      </li>
      <li className={isActive('game-drops')} role='presentation'>
        <a data-toggle='tab' href='#game-drops' onClick={() => setShowDrops(true)}>Drops</a>
      </li>
      { (game.number_of_players >= game.max_number_of_players || (props.waiting_players && props.waiting_players.length) > 0) &&
        <li className={isActive('game-waitlist')} role='presentation'>
          <a data-toggle='tab' href='#game-waitlist' onClick={() => setShowWaitList(true)}>
            Waitlist {props.waiting_players && props.waiting_players.length > 0 ? props.waiting_players.length : ''}
          </a>
        </li>
      }
    </ul>

    <div className='tab-content'>
      <div id='game-details' className={'tab-pane fade in' + isActive('game-details')}>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert} />}
          { !showAnnouncements &&
            <GameDetails 
              game={game}
              playerReservations={props.player_reservations}
              onUpdatePR={props.onUpdatePR}
              onEditCost={props.onEditCost}
              onChangeGuests={props.onChangeGuests}
              onRemovePlayer={props.onRemovePlayer}
              onProcessAll={() => handleDebouncedClick(props.onProcessAll)}
              onCaptureAll={() => handleDebouncedClick(props.onCaptureAll)}
              roasterChanged={props.roasterChanged}
              onShowPlayer={onShowPlayer}
              onDuplicate={props.onDuplicate}
              onAnnouncements={() => setShowAnnouncements(true)}
              onAddFake={props.onAddFake}
              onEdit={props.onEdit}
              onShowPayment={(id) => props.onStartPayment(id)}
              isLoading = {props.gameDetailsLoading}
              showAlert={props.showAlert}
            />
          }
          { showAnnouncements &&
            <GameAnnouncements
              game={game}
              onClose={() => setShowAnnouncements(false)}
              onShowPlayer={onShowPlayer}
              onAlert={setAlert}
            />
          }
        </Modal.Body>
        <Modal.Footer>
          <GameDetailsFooter 
            game={game} 
            onClose={props.onClose}
            onConfirm={confirmGame}
            onCancel={() => setShowCancelModal(true)}
            onUpdateWithFacility={confirmWithFacility}
            onPreBook={preBook}
          />
        </Modal.Footer>
        { googleConflict &&
          <GoogleConflictWarning
            onClose={() => setGoogleConflict(null)}
            googleConflict={googleConflict}
            onConfirm={forceConfirm}
          />
        }
      </div>
      <div id='game-history' className={'tab-pane fade' + isActive('game-history')}>
        <Modal.Body>
          { showEvents && <GameEvents events={props.events} onLoad={props.onLoadEvents} /> }
        </Modal.Body>
      </div>
      <div id='game-recurrings' className={'tab-pane fade' + isActive('game-recurrings')}>
        <Modal.Body>
          { showReccurings && 
            <RecurringReservations 
              reservationId={game.id}
              defaultDate={game.date}
              onEdit={(data) => props.onEdit({ type: 'recurring', id: data.gameId, recurringId: data.recurringId})}
              onChanged={props.onClose}
            />
          }
        </Modal.Body>
      </div>
      <div id='game-comment' className={'tab-pane fade' + isActive('game-comment')}>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert}/> }
          { showNotes &&
            <GameNotes
              notes={props.notes}
              onLoadNotes={props.onLoadNotes}
              onCreateNote={props.onCreateNote} 
              onEditNote={props.onEditNote} 
              onRemoveNote={props.onRemoveNote} 
            />
          }
        </Modal.Body>
      </div>
      <div id='game-reviews' className={'tab-pane fade' + isActive('game-reviews')}>
        <Modal.Body>
          { showReviews && <GameReviews reviews={props.reviews} onLoad={props.onLoadReviews} /> }
        </Modal.Body>
      </div>
      <div id='game-messages' className={'tab-pane fade' + isActive('game-messages')}>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert} />}
          { game && game.channel_id &&
            <FirestoreChannel
              channelId={game.channel_id}
              adminPlayerId={game.admin_player_id}
              onShowPlayer={onShowPlayer}
              scroll='bottom' />
          }
          { game && !game.channel_id && <div className='text-center'>
            <a className='btn btn-info clickable' onClick={createChannel}>Create Channel</a></div>
          }
        </Modal.Body>
      </div>
      <div id='game-drops' className={'tab-pane fade' + isActive('game-drops')}>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert} />}
          { showDrops && <GameDrops
              game={game}
              drops={props.drops}
              onLoad={props.onLoadDrops}
              onShowPlayer={onShowPlayer}
              onSplitDrop={props.onSplitDrop}
          /> }
        </Modal.Body>
      </div>
      <div id='game-waitlist' className={'tab-pane fade' + isActive('game-waitlist')}>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={props.clearAlert} />}
          { showWaitList && <GameWaitingPlayers
              reservationId={game.id}
              waitingPlayers={props.waiting_players}
              expanded={canExpand}
              onCreateWaitingPlayer={props.onCreateWaitingPlayer}
              onRemoveWaitingPlayer={props.onRemoveWaitingPlayer}
              playerRefresh={props.onSetRefreshFunc}
          /> }
        </Modal.Body>
      </div>
    </div>
  </Modal>
}

GameModal.propTypes = {
  activeTab: PropTypes.string,
  gameId: PropTypes.number,
  game: PropTypes.object,
  player_reservations: PropTypes.array,
  waiting_players: PropTypes.array,
  events: PropTypes.array,
  notes: PropTypes.array,
  reviews: PropTypes.array,
  socketUrl: PropTypes.string,
  alert: PropTypes.object,
  payment: PropTypes.number,
  refreshFunc: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  loadGame: PropTypes.func.isRequired,
  clearGame: PropTypes.func.isRequired,
  roasterChanged: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  clearAlert: PropTypes.func.isRequired,
  onChangeGuests: PropTypes.func.isRequired,
  onUpdatePR: PropTypes.func.isRequired,
  onEditCost: PropTypes.func.isRequired,
  onAddPlayer: PropTypes.func.isRequired,
  onRemovePlayer: PropTypes.func.isRequired,
  onCreateNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func.isRequired,
  onRemoveNote: PropTypes.func.isRequired,
  onProcessAll: PropTypes.func.isRequired,
  onCaptureAll: PropTypes.func.isRequired,
  onStartPayment: PropTypes.func.isRequired,
  onClearPayment: PropTypes.func.isRequired,
  updateGame: PropTypes.func.isRequired,
  onUpdatePlayer: PropTypes.func.isRequired,
  onSetRefreshFunc: PropTypes.func.isRequired,
  readMessages: PropTypes.func.isRequired,
  onLoadEvents: PropTypes.func.isRequired,
  onLoadNotes: PropTypes.func.isRequired,
  onLoadReviews: PropTypes.func.isRequired,
  onLoadDrops: PropTypes.func.isRequired,
  setGameDetailsLoading: PropTypes.func.isRequired,
  onSetWaitingPlayers: PropTypes.func.isRequired,
  onCreateWaitingPlayer: PropTypes.func.isRequired,
  onRemoveWaitingPlayer: PropTypes.func.isRequired,
  onSplitDrop: PropTypes.func.isRequired,
}

export default GameModal
